import {
  ChangeEvent,
  CSSProperties,
  KeyboardEvent,
  MouseEvent,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import { AccentColorName } from 'apps/cdp-web-demo/app/types';
import { RadioProps } from '@cbhq/cds-web/controls/Radio';
import { Text } from '@cbhq/cds-web/typography';

import styles from './Color.module.css';

type ColorOption = AccentColorName | 'customColor' | `customColor--#${string}`;

function RadioWithColorInput({
  accentColor,
  testID,
  ...props
}: Omit<RadioProps<ColorOption>, 'color'> & { accentColor: string }) {
  const { onChange, onClick, onKeyUp } = props;
  const colorInputId = useId();
  const colorLabelRef = useRef<HTMLLabelElement>(null);
  const colorInputRef = useRef<HTMLInputElement>(null);
  const radioInputRef = useRef<HTMLInputElement>(null);
  const style = { '--radio-accent-color': accentColor } as CSSProperties;
  const [pendingColor, setPendingColor] = useState<string | null>(null);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Debounce the onChange callback
  useEffect(() => {
    if (pendingColor !== null) {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      debounceTimeoutRef.current = setTimeout(() => {
        const syntheticEvent = {
          target: {
            value: `customColor--${pendingColor}`,
          },
        } as ChangeEvent<HTMLInputElement>;
        onChange?.(syntheticEvent);
        setPendingColor(null);
      }, 150);
    }

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [pendingColor, onChange]);

  const handleColorChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setPendingColor(e.target.value);
  }, []);
  const handleClickLabel = useCallback(() => {
    colorLabelRef.current?.click();
  }, []);
  const handleClick = useCallback(
    (e: MouseEvent<HTMLInputElement>) => {
      onClick?.(e);
      colorLabelRef.current?.click();
    },
    [onClick],
  );
  const handleWrapperClick = useCallback(() => {
    colorLabelRef.current?.click();
  }, []);
  const handleKeyUp = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      onKeyUp?.(e);
      if (e.key === ' ') {
        colorInputRef.current?.click();
      }
    },
    [onKeyUp],
  );
  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
    <div className={styles['radio-with-color-input']} onClick={handleWrapperClick}>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */}
      <label
        className={`${styles['radio-wrapper']} ${accentColor ? styles['accent-color'] : ''}`}
        style={style}
        htmlFor={props.id}
        onClick={handleClickLabel}
      >
        <input
          key={`${props.value}-radio`}
          type="radio"
          ref={radioInputRef}
          {...props}
          data-testid={testID}
          onClick={handleClick}
          onKeyUp={handleKeyUp}
          className="sr-only"
        />
        <span className={styles['radio-icon']} />
      </label>
      <label key={`${props.value}-color-label`} htmlFor={colorInputId} ref={colorLabelRef}>
        <input
          key={`${props.value}-color`}
          id={colorInputId}
          ref={colorInputRef}
          aria-label="Custom color"
          type="color"
          value={accentColor.startsWith('#') ? accentColor : '#000000'}
          onChange={handleColorChange}
          className={styles['color-input']}
          tabIndex={-1}
        />
      </label>
      <Text font="body" as="span" color={accentColor ? 'fg' : 'fgMuted'}>
        {!!accentColor && <span className={styles['color-hex']}>{accentColor.slice(1)}</span>}
        {!accentColor && <span>Custom</span>}
      </Text>
    </div>
  );
}

function Radio({
  accentColor,
  testID,
  ...props
}: Omit<RadioProps<ColorOption>, 'color'> & { accentColor: string }) {
  const radioInputRef = useRef<HTMLInputElement>(null);
  const style = { '--radio-accent-color': accentColor } as CSSProperties;

  if (props.value === 'customColor') {
    return <RadioWithColorInput accentColor={accentColor} testID={testID} {...props} />;
  }

  return (
    <label
      className={`${styles['radio-wrapper']} ${styles['radio-ring']} ${accentColor ? styles['accent-color'] : ''}`}
      style={style}
      htmlFor={props.id}
    >
      <input
        key={`${props.value}-radio`}
        type="radio"
        ref={radioInputRef}
        {...props}
        data-testid={testID}
        className="sr-only"
      />
      <span className={styles['radio-icon']} />
    </label>
  );
}

export { Radio, RadioWithColorInput };
